import type { ReactElement } from 'react';
import type { MarketReachView } from '../../../domain/demand/marketViews';
import type {
  AirportView,
  FleetAircraftView,
  RouteAnalyticsView,
  RouteSeatShareView,
} from '../../../domain/models/contracts';
import { DarkSectionHeader } from '../common/DarkSectionHeader';
import { KpiSummaryStrip } from '../common/KpiSummaryStrip';
import { aircraftMix, hubKpis, trafficLeaders, weeklyPerformance } from './hubMetrics';
import { PERFORMANCE_COLORS } from '../../styles/tokens';

export interface HubOverviewProps {
  readonly airportId: string;
  readonly airport: AirportView | undefined;
  readonly tierLabel: string;
  readonly weeklyDepartures: number;
  readonly routes: readonly RouteAnalyticsView[];
  readonly airportsById: Readonly<Record<string, AirportView>>;
  readonly competitorShares: readonly RouteSeatShareView[];
  readonly fleet: readonly FleetAircraftView[];
  readonly typeNames: Readonly<Record<string, string>>;
  readonly reach: MarketReachView | null;
}

const INT = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

/** Header card, KPI strip, traffic leaders, aircraft mix and weekly performance for the selected hub. */
export function HubOverview(props: HubOverviewProps): ReactElement {
  const { airportId, airport, routes } = props;
  const kpis = hubKpis(airportId, routes, airport, props.weeklyDepartures);
  const leaders = trafficLeaders(airportId, routes, props.airportsById, props.competitorShares, 10);
  const mix = aircraftMix(routes, props.fleet, props.typeNames);
  const perf = weeklyPerformance(routes);
  const segments = [
    { label: 'On time', pct: perf.onTimePct, color: PERFORMANCE_COLORS.onTime },
    { label: 'Minor delays', pct: perf.minorPct, color: PERFORMANCE_COLORS.minor },
    { label: 'Moderate delays', pct: perf.moderatePct, color: PERFORMANCE_COLORS.moderate },
    { label: 'Major / cancelled', pct: perf.majorPct, color: PERFORMANCE_COLORS.major },
  ] as const;

  return (
    <div className="hub-overview">
      <header className="fl-card hub-overview-head">
        <span className="fl-iata">{airportId}</span>
        <div>
          <h3 className="fl-section-title">{airport?.name ?? airportId}</h3>
          <p className="fl-sub">{airport ? `${airport.city}, ${airport.country}` : 'Unknown airport'} · {props.tierLabel}</p>
        </div>
      </header>

      <KpiSummaryStrip
        weeklyPassengers={kpis.weeklyPassengers}
        movementsLabel={`${INT.format(kpis.weeklyMovements)} movements`}
        loadFactorPct={kpis.loadFactorPct}
        weeklySeats={kpis.weeklySeats}
        activeRoutes={kpis.activeRoutes}
        connectedAirports={kpis.connectedAirports}
        marketPopulation={props.reach?.marketReachPopulation ?? null}
        eligibleTravelers={props.reach?.eligibleTravelers ?? null}
        congestion={kpis.congestion}
        pressurePct={kpis.pressurePct}
      />

      <div className="hub-overview-cols">
        <DarkSectionHeader title="Traffic leaders" subtitle="Busiest routes from this airport" summary={`${leaders.length} routes`}>
          {leaders.length === 0 ? (
            <p className="fl-sub">No published routes touch this airport.</p>
          ) : (
            <table className="fl-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Route</th>
                  <th>Airport &amp; city</th>
                  <th>Flights / week</th>
                  <th>Airlines</th>
                </tr>
              </thead>
              <tbody>
                {leaders.map((row) => (
                  <tr key={row.otherAirportId}>
                    <td>{row.rank}</td>
                    <td>
                      <span className="fl-iata">{row.otherAirportId}</span>
                    </td>
                    <td>
                      {row.name}
                      {row.city !== '' && <span className="fl-sub"> · {row.city}</span>}
                    </td>
                    <td>{INT.format(row.flightsPerWeek)}</td>
                    <td>{row.airlines}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </DarkSectionHeader>

        <aside className="fl-card hub-mix" aria-label="Aircraft mix">
          <h3 className="fl-section-title">Aircraft mix</h3>
          {mix.length === 0 ? (
            <p className="fl-sub">No aircraft flying here yet.</p>
          ) : (
            <ul className="hub-mix-list">
              {mix.map((row) => (
                <li key={row.typeName}>
                  <div className="hub-mix-line">
                    <span>{row.typeName}</span>
                    <span className="fl-sub">
                      {INT.format(row.flightsPerWeek)} flights · {row.sharePct.toFixed(1)}%
                    </span>
                  </div>
                  <div className="hub-mix-track">
                    <div className="hub-mix-fill" style={{ width: `${row.sharePct}%` }} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </aside>
      </div>

      <section className="fl-card hub-perf" aria-label="Weekly performance">
        <h3 className="fl-section-title">Weekly performance</h3>
        <div
          className="hub-perf-bar"
          role="img"
          aria-label={segments.map((s) => `${s.label} ${s.pct.toFixed(1)} percent`).join(', ')}
        >
          {segments
            .filter((s) => s.pct > 0)
            .map((s) => (
              <span key={s.label} style={{ width: `${s.pct}%`, background: s.color }} title={`${s.label}: ${s.pct.toFixed(1)}%`} />
            ))}
        </div>
        <ul className="hub-perf-legend">
          {segments.map((s) => (
            <li key={s.label}>
              <span className="fl-dot" style={{ background: s.color }} />
              {s.label} <strong className="fl-num">{s.pct.toFixed(1)}%</strong>
            </li>
          ))}
        </ul>
        <p className="fl-sub">
          {INT.format(perf.scheduled)} flights scheduled a week. Delays are not simulated yet, so operated flights count as on time.
        </p>
      </section>
    </div>
  );
}
